import { consoleLogger } from '../logging/consoleLogger';

export const validateTemplate = ({
  fieldsTemplate = [],
  validationTargetObject,
  logIdentificator,
}: {
  fieldsTemplate: { key: string; isOptional: boolean; type: string }[];
  validationTargetObject: object;
  logIdentificator: string;
}): { isValid: boolean } => {
  if (!logIdentificator) {
    consoleLogger.logError('Missing log identificator');
    return { isValid: false };
  }
  if (!validationTargetObject) {
    consoleLogger.logError(`Missing validation target for ${logIdentificator}`);
    return { isValid: false };
  }
  if (typeof validationTargetObject !== 'object') {
    consoleLogger.logError(`Wrong type for ${validationTargetObject}`);
    return { isValid: false };
  }
  if (fieldsTemplate === null) {
    consoleLogger.logError(
      `Fields template were set to ${fieldsTemplate} for ${logIdentificator} and cannot be validated. Returning with negative validation result...`,
    );
    return { isValid: false };
  }
  if (fieldsTemplate.length === 0) {
    consoleLogger.logDebug(
      `No fields template provided for ${logIdentificator}. Continuing without fields validation...`,
    );
  }

  const validationTargetKeys: string[] = Object.keys(validationTargetObject);
  const fieldsTemplateKeys: string[] = fieldsTemplate.map((field) => field.key);
  const isCoveringAllValidationTargetKeys = validationTargetKeys.every(
    (validationTargetKey) => fieldsTemplateKeys.includes(validationTargetKey),
  );
  if (!isCoveringAllValidationTargetKeys) {
    const missingFields = validationTargetKeys.filter(
      (item) => !fieldsTemplateKeys.includes(item),
    );
    consoleLogger.logDebug('Validation target keys:', validationTargetKeys);
    consoleLogger.logDebug('Fields template keys:', fieldsTemplateKeys);
    consoleLogger.logError(
      `Validation target has more fields than present in ${logIdentificator} fields template.`,
      'Missing fields:',
      missingFields,
    );
    return { isValid: false };
  }

  const errorMessages: string[] = [];
  try {
    fieldsTemplate.forEach((field) => {
      if (
        typeof field.key !== 'string' ||
        typeof field.isOptional !== 'boolean' ||
        typeof field.type !== 'string'
      ) {
        errorMessages.push(
          `Field template ${JSON.stringify(
            field,
          )} is of incorrect format for ${logIdentificator}`,
        );
        return;
      }

      const fieldKey: string = field.key;

      const targetField: never =
        validationTargetObject[fieldKey as keyof typeof validationTargetObject];
      if (targetField === null || targetField === undefined) {
        if (!field.isOptional) {
          errorMessages.push(
            `Field ${field.key} is missing for ${logIdentificator}`,
          );
        }
        /* If the value is not defined, we should not proceed for both optional and compulsory fields */
        return;
      }
      const isValidFieldType: boolean =
        field.type === 'array'
          ? Array.isArray(targetField)
          : typeof targetField === field.type && !Array.isArray(targetField); // In case of an object that should not be an array
      if (!isValidFieldType) {
        errorMessages.push(
          `Field ${field.key} expected type ${
            field.type
          }, but got ${typeof validationTargetObject[
            fieldKey as keyof typeof validationTargetObject
          ]} for ${logIdentificator}`,
        );
      }
    });
  } catch (error: unknown) {
    if (
      typeof error === 'object' &&
      error !== null &&
      'message' in error &&
      typeof error.message === 'string'
    )
      errorMessages.push(error.message);
  }

  errorMessages.forEach((errorMessage) => {
    consoleLogger.logError(errorMessage);
  });

  return { isValid: errorMessages.length === 0 };
};
